import { useEffect } from 'react';

import moment from 'moment';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircle } from 'react-bootstrap-icons';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateParser, getComponentFormError } from '../../../utils';

import 'react-datepicker/dist/react-datepicker.css';
import './style.scss';

const transalationDays = 'common.label.days.';
const transalationMoth = 'common.label.months.';

interface Props {
  label: string;
  name: string;
  description?: string;
  defaultValue?: Date;
  placeholder?: string;
  customError?: any;
  readOnly?: boolean;
  onBlur?: (date: Date | [Date, Date] | null) => void;
}

const Datepicker = (props: Props) => {
  const {
    formState: { errors },
    control
  } = useFormContext();
  const { t } = useTranslation();
  const { label, name, description, defaultValue, customError, readOnly } = props;
  const error = getComponentFormError(name, errors);

  useEffect(() => {
    const monthsBG = [
      t(transalationMoth + 'january'),
      t(transalationMoth + 'february'),
      t(transalationMoth + 'march'),
      t(transalationMoth + 'april'),
      t(transalationMoth + 'may'),
      t(transalationMoth + 'june'),
      t(transalationMoth + 'july'),
      t(transalationMoth + 'august'),
      t(transalationMoth + 'september'),
      t(transalationMoth + 'october'),
      t(transalationMoth + 'november'),
      t(transalationMoth + 'december')
    ];
    const daysBG = [
      t(transalationDays + 'sunday'),
      t(transalationDays + 'monday'),
      t(transalationDays + 'tuesday'),
      t(transalationDays + 'wednesday'),
      t(transalationDays + 'thursday'),
      t(transalationDays + 'friday'),
      t(transalationDays + 'saturday')
    ];

    registerLocale('bg', {
      localize: {
        month: (n: any) => monthsBG[n],
        day: (n: any) => daysBG[n],
        ordinalNumber: (n: any) => String(n),
        era: (n: any) => String(n),
        quarter: (n: any) => String(n),
        dayPeriod: (n: any) => String(n)
      },
      formatLong: {
        date: () => 'dd/MM/yyyy',
        time: () => 'HH:mm:ss',
        dateTime: () => 'dd/MM/yyyy HH:mm:ss'
      },
      match: {
        ordinalNumber: () => ({ value: 1, rest: '' }),
        era: () => ({ value: 1, rest: '' }),
        quarter: () => ({ value: 1, rest: '' }),
        month: () => ({ value: 1, rest: '' }),
        day: () => ({ value: 1, rest: '' }),
        dayPeriod: () => ({ value: 1, rest: '' })
      } as any,
      options: {
        weekStartsOn: 1
      }
    });
  }, [t]);

  const render = ({ field }: any) => {
    const datevalue =
      field.value instanceof Date ? field.value : field.value ? moment(field.value).toDate() : null;

    return (
      <div
        className={
          error || customError ? 'customDatePickerWidth is-invalid' : 'customDatePickerWidth '
        }>
        <DatePicker
          showMonthDropdown
          showYearDropdown
          readOnly={readOnly}
          selected={datevalue}
          locale="bg"
          dateFormat={DateParser.dateFormat}
          className={error || customError ? 'form-control is-invalid ' : 'form-control'}
          onChange={(date: Date | null) => {
            if (props.onBlur) {
              props.onBlur(date);
            }
            field.onChange(date);
          }}
          onBlur={field.onBlur}
        />
      </div>
    );
  };

  return (
    <Form.Group>
      <Form.Label>
        {label}{' '}
        {description ? (
          <OverlayTrigger overlay={<Tooltip id={`tooltip-${name}`}>{description}</Tooltip>}>
            <span className="d-inline-block">
              <InfoCircle className="small"></InfoCircle>
            </span>
          </OverlayTrigger>
        ) : (
          <></>
        )}
      </Form.Label>

      <Controller control={control} name={name} defaultValue={defaultValue} render={render} />

      <Form.Control.Feedback type="invalid">
        {error ? error.message : customError ? customError.message : ''}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

export default Datepicker;
